import {
  getSocket,
  addSocketOpenListener,
  addSocketReplacedListener,
  addSessionEndedListener,
} from './channelService';

/**
 * Internal (client-generated) event dispatched when the workspace channel
 * rejoins after a socket reconnect — broadcasts sent while disconnected are
 * never replayed, so subscribers should refetch server-owned state.
 */
export const WORKSPACE_REJOINED_EVENT = '__workspace_rejoined';

let workspaceChannel = null;
let joinedWorkspaceId = null;
let desiredWorkspaceId = null;
let pendingJoinUnsub = null;
let socketReplacedUnsub = null;
const eventHandlers = new Map();

function ensureHandlerSet(event) {
  if (!eventHandlers.has(event)) {
    eventHandlers.set(event, new Set());
  }
  return eventHandlers.get(event);
}

function dispatchEvent(event, payload) {
  ensureHandlerSet(event).forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      console.error(`Workspace channel handler error (${event}):`, err);
    }
  });
}

function attachChannelListeners(channel) {
  // Phoenix has no native wildcard binding, but onMessage runs for every
  // inbound frame. Forwarding all application events (skipping Phoenix's
  // internal phx_*/chan_reply_* frames) keeps this service agnostic to event
  // names: new backend broadcasts reach consumers with no change here, and
  // consumers subscribe only to the events they handle.
  channel.onMessage = (event, payload) => {
    if (typeof event === 'string' && !event.startsWith('phx_') && !event.startsWith('chan_reply_')) {
      dispatchEvent(event, payload);
    }
    return payload;
  };
}

function clearPendingJoin() {
  if (pendingJoinUnsub) {
    pendingJoinUnsub();
    pendingJoinUnsub = null;
  }
}

// COMMS-04: a region failover replaces channelService's socket outright, but
// `workspaceChannel` still points at a Channel bound to the old, now-dead
// one — the "already joined" guard below would treat that stale reference as
// success forever and silently stop delivering realtime for the open
// workspace. On this signal, drop the stale reference (no leave() — there is
// nothing listening on the dead transport) and rejoin the desired workspace
// for real on the new socket.
function ensureSocketReplacedRetry() {
  if (socketReplacedUnsub) return;
  socketReplacedUnsub = addSocketReplacedListener(() => {
    if (!desiredWorkspaceId) return;
    workspaceChannel = null;
    joinedWorkspaceId = null;
    joinWorkspaceChannel(desiredWorkspaceId);
  });
}

// A session end clears channelService's socketReplacedHandlers Set, but
// ensureSocketReplacedRetry() above only ever registers into it ONCE per
// module lifetime (guarded by socketReplacedUnsub). Without resetting that
// guard too, it stays non-null forever after the first session end, so no
// later login this app session ever re-subscribes — silently reintroducing
// COMMS-04 (a later failover swaps in a socket nobody told this service
// about) starting from the second login.
//
// Guard-only was not enough on its own, though (see addSessionEndedListener's
// invariant doc): `workspaceChannel`/`joinedWorkspaceId`/`desiredWorkspaceId`
// are this module's PRIMARY state, and until this fix they were reset only
// by leaveWorkspaceChannel() being called from useWorkspaceRealtime.js's own
// effect cleanup (keyed on isAuthenticated) — a mounted-component dependency,
// not a guarantee. A session end that isn't immediately followed by that
// effect re-running left the departed account's joined workspace live for
// whoever signs in next on this device. leaveWorkspaceChannel() resets
// everything this module mirrors, so calling it here — in addition to
// nulling the guard — is what makes this module actually reset ITSELF.
//
// Registered once, unconditionally, at module load — this subscription
// itself must survive every teardown, so it does not go through the guarded
// ensureSocketReplacedRetry().
addSessionEndedListener(() => {
  socketReplacedUnsub = null;
  leaveWorkspaceChannel();
});

export function joinWorkspaceChannel(workspaceId) {
  if (!workspaceId) {
    leaveWorkspaceChannel();
    return Promise.resolve();
  }

  ensureSocketReplacedRetry();

  if (joinedWorkspaceId === workspaceId && workspaceChannel) {
    return Promise.resolve();
  }

  leaveWorkspaceChannel();
  desiredWorkspaceId = workspaceId;

  const socket = getSocket();
  if (!socket) {
    // The socket is created during login and may not exist yet when a workspace
    // opens immediately on launch. Retry the join once the socket connects so
    // realtime sync is not silently dead for the session.
    pendingJoinUnsub = addSocketOpenListener(() => {
      clearPendingJoin();
      if (desiredWorkspaceId) joinWorkspaceChannel(desiredWorkspaceId);
    });
    return Promise.resolve();
  }

  const channel = socket.channel(`workspace:${workspaceId}`, {});
  workspaceChannel = channel;
  joinedWorkspaceId = workspaceId;
  attachChannelListeners(channel);

  return new Promise((resolve) => {
    // Phoenix re-sends the join push after a socket reconnect and re-fires its
    // receive hooks, so a second 'ok' on the same channel means we rejoined
    // after a gap in which broadcasts may have been missed. Surface that as an
    // internal event so consumers can resync state they own.
    let joinOkCount = 0;
    channel
      .join()
      .receive('ok', () => {
        joinOkCount += 1;
        if (joinOkCount > 1) {
          dispatchEvent(WORKSPACE_REJOINED_EVENT, { workspace_id: workspaceId });
        }
        resolve();
      })
      .receive('error', (err) => {
        console.warn('Workspace channel join error:', err);
        resolve();
      })
      .receive('timeout', () => {
        console.warn('Workspace channel join timed out');
        resolve();
      });
  });
}

export function leaveWorkspaceChannel() {
  clearPendingJoin();
  desiredWorkspaceId = null;
  if (workspaceChannel) {
    workspaceChannel.leave();
  }
  workspaceChannel = null;
  joinedWorkspaceId = null;
}

export function getJoinedWorkspaceId() {
  return joinedWorkspaceId;
}

export function addWorkspaceEventListener(event, fn) {
  ensureHandlerSet(event).add(fn);
  return () => eventHandlers.get(event)?.delete(fn);
}

export function removeAllWorkspaceEventListeners() {
  eventHandlers.clear();
}

// REMOVED 03.08.2026: pushWorkspacePresenceContext.
//
// It pushed "I'm viewing request X" so teammates could watch which request you
// had open. Realtime collaboration is deliberately out of scope for the request
// builder, and this had no client-side consumer anyway — the viewer avatar
// stack was removed earlier, so all it did was report your focus to the server.
//
// The backend still handles `presence:context` on both the workspace and team
// channels, on purpose: Phoenix crashes a channel on an unmatched handle_in and
// shipped 0.1.13 clients still push it. Do not remove those handlers until no
// client in the wild sends this.
